import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core/i18n';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';

type Props = Readonly<{
  href: string;
  previewScale?: number;
  maxWidth?: number;
  // 'editor' (default): admin inline-editor, offers a link; 'preview': live MCP draft preview, text only (the link would be a dead proxy route).
  variant?: 'editor' | 'preview';
}>;

export const PreviewDisabledTooltipContent: React.FC<Props> = ({ href, previewScale, maxWidth, variant = 'editor' }) => {
  const { tq } = useTranslation('eventMenu');
  const inverseScale = previewScale && previewScale > 0 ? 1 / previewScale : 1;
  const cleanHref = href.split('?')[0];

  const innerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const node = innerRef.current;
    if (!node) return;
    const measure = () => setSize({ width: node.offsetWidth, height: node.offsetHeight });
    measure();
    const ro = createSafeResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // re-measure when scale changes since text metrics can shift rendering
  useEffect(() => {
    const node = innerRef.current;
    if (!node) return;
    setSize({ width: node.offsetWidth, height: node.offsetHeight });
  }, [previewScale]);

  const outerWidth = size ? size.width * inverseScale : undefined;
  const outerHeight = size ? size.height * inverseScale : undefined;

  return (
    <Box
      style={{ width: outerWidth, height: outerHeight }}
      __css={{ 'a::after, a:hover::after, a:active::after': { display: 'none', content: 'none', background: 'none' } }}
    >
      <Box
        ref={innerRef}
        style={{
          display: 'inline-block',
          transform: `scale(${inverseScale})`,
          transformOrigin: 'top left',
          maxWidth,
        }}
      >
        {variant === 'preview' ? (
          <TextV2 typographyVariant="label2">{tq`This page isn't available in preview.`}</TextV2>
        ) : (
          <TextV2 typographyVariant="label2">
            {tq`This page can't be previewed in the editor.`}{' '}
            <TextV2
              as="a"
              typographyVariant="label2"
              href={cleanHref}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'underline', color: 'inherit', cursor: 'pointer' }}
            >
              {tq`View in guest preview`}
            </TextV2>
          </TextV2>
        )}
      </Box>
    </Box>
  );
};
